// SOC threat-map shared display helpers — KAN-227.
//
// Small pure formatters reused across the live feed, drawers, and stage views
// so timestamp/severity/status presentation stays consistent. Every colour is
// returned as an IntelHub design-token custom property (never a literal hex),
// so dark/light parity is automatic and the no-hardcoded-colour convention
// holds.
import type {
  Incident,
  Severity,
  ThreatMapAvailableButNotSelected,
  ThreatMapProvenanceSource,
  ThreatMapUnknownReason,
} from '@/types/map'

/** Epoch-ms → UTC `HH:MM:SS` clock (matches the prior inline `.slice(11, 19)`). */
export function clockUTC(ms: number): string {
  return new Date(ms).toISOString().slice(11, 19)
}

/** Epoch-ms → UTC `YYYY-MM-DD` date (matches the prior inline `.slice(0, 10)`). */
export function dateUTC(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/** Severity → its `--severity-*` design-token custom property. */
export function severityVar(sev: Severity): string {
  return `var(--severity-${sev})`
}

/**
 * Incident lifecycle status → a design-token custom property used for the dot,
 * label, and tinted status pill. Active attacks read as the loudest signal;
 * unattributed activity is intentionally muted.
 */
export function incidentStatusVar(status: Incident['status']): string {
  switch (status) {
    case 'active':
      return 'var(--severity-critical)'
    case 'contained':
      return 'var(--severity-medium)'
    case 'resolved':
      return 'var(--status-resolved)'
    case 'unattributed':
    default:
      return 'var(--text-tertiary)'
  }
}

export function incidentAttributionLabel(incident: Incident, actorName?: string | null): string {
  if (actorName) return actorName
  if (incident.ransomwareGroupName) return incident.ransomwareGroupName
  return 'UNATTRIB'
}

export function incidentAttributionQualifier(incident: Incident): string | null {
  if (incident.attributionState === 'ransomware_group_actor_unlinked') {
    return 'Known ransomware group · actor link pending'
  }
  if (incident.sourceKind === 'leak_site_claim') {
    return 'Leak-site claim'
  }
  return null
}

const REASON_LABELS: Record<ThreatMapUnknownReason, string> = {
  actor_missing: 'Actor missing',
  victim_name_missing: 'Victim missing',
  geo_source_fallback_only: 'Geo fallback only',
  geo_read_flag_disabled: 'Geo read disabled',
  no_renderable_geo: 'No renderable geo',
}

const SOURCE_LABELS: Record<ThreatMapProvenanceSource, string> = {
  story: 'Story',
  article: 'Article',
  threat_actor: 'Threat actor',
  geo_facet: 'Geo facet',
  ransomware_group: 'Ransomware group',
  leak_site_claim: 'Leak-site claim',
  fallback: 'Fallback',
}

export function projectionReasonLabel(reason: ThreatMapUnknownReason): string {
  return REASON_LABELS[reason]
}

export function provenanceSourceLabel(source: ThreatMapProvenanceSource): string {
  return SOURCE_LABELS[source]
}

export function provenanceFieldLabel(field: string): string {
  return field.replace(/[._]/g, ' ')
}

export function formatAvailableButNotSelected(entry: ThreatMapAvailableButNotSelected): string {
  const reasons = entry.reasons.map(projectionReasonLabel).join(', ')
  return `${entry.domain}: ${entry.count} not selected${reasons ? ` (${reasons})` : ''}`
}

export function incidentProjectionSummary(incident: Incident): string | null {
  const reasons = [...new Set(incident.unknownReasons ?? [])]
  if (reasons.length > 0) return reasons.map(projectionReasonLabel).join(' · ')

  const selected = incident.provenance?.find((entry) => entry.selected) ?? incident.provenance?.[0]
  if (selected) return selected.reason ?? provenanceFieldLabel(selected.field)

  return null
}
